import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { BASE_URL } from '@/constants/constant';

import deploymentDetector from '@/services/deploymentDetector';

const baseQuery = fetchBaseQuery({
	baseUrl: BASE_URL,
	prepareHeaders: (headers) => {
		const token = localStorage.getItem('token');
		if (token) {
			headers.set('Authorization', token);
		}

		return headers;
	}
});

// Wrap baseQuery to track deployment errors
const baseQueryWithDeploymentDetection = async (args, api, extraOptions) => {
	// If already in deployment mode (and browser is online), don't even attempt the request
	// Skip this check when offline — it's a network issue, not a deployment
	if (deploymentDetector.isDeploying() && navigator.onLine) {
		return {
			data: undefined,
			meta: { request: {}, response: {} }
		};
	}

	const result = await baseQuery(args, api, extraOptions);

	if (result.error) {
		const status = result.error.status;

		// Only check for ACTUAL deployment errors:
		// - FETCH_ERROR: network failures (server unreachable, CORS, connection refused)
		// - 502/503: server deployment status codes
		// Regular HTTP errors (400, 401, 404, 500, etc.) must pass through
		// so RTK Query hooks (isError, .unwrap(), onQueryStarted) work correctly.
		const isNetworkError = status === 'FETCH_ERROR';
		const isServerDeployError = status === 502 || status === 503;

		if (isNetworkError || isServerDeployError) {
			const error = {
				message: result.error.error || 'Network Error',
				code: isNetworkError ? 'ERR_NETWORK' : undefined,
				response: isServerDeployError ? { status } : undefined,
				request: isNetworkError ? {} : undefined
			};

			const isDeploymentError = deploymentDetector.recordError(error);

			// Suppress deployment errors to prevent RTK Query from retrying
			if (isDeploymentError) {
				return {
					data: undefined,
					meta: { request: {}, response: {} }
				};
			}
		}
	} else {
		// Record successful API call
		deploymentDetector.recordSuccess();
	}

	return result;
};

export const baseApi = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithDeploymentDetection,
	// Disable automatic refetching to prevent performance issues during deployment
	refetchOnMountOrArgChange: false,
	refetchOnFocus: false,
	refetchOnReconnect: false,
	// Keep data for 5 minutes to reduce unnecessary requests
	keepUnusedDataFor: 300,
	tagTypes: [
		'MoodBoards',
		'FactoryPOInfo',
		'Target',
		'Breakdowns',
		'MATERIAL_REQUISITIONS',
		'MATERIAL_REQUISITION_PRELIMINARY_ITEMS',
		'MATERIAL_REQUISITION_SUMMARY',
		'PROJECT_TASKS',
		'PROJECT_GROUPS',
		'BRAND_PROFILE_CLIENTS',
		'Costing',
		'History'
	],
	endpoints: () => ({})
});

export default baseApi;
